import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useAdminAuth } from '../context/AdminAuthContext'

const navMaster = [
  { to: '/', label: 'Visão geral', icon: 'fa-table-cells-large', end: true, group: 'Painel' },
  { to: '/tenants', label: 'Clientes', icon: 'fa-building', group: 'Gestão' },
  { to: '/revendas', label: 'Revendas', icon: 'fa-handshake', group: 'Gestão' },
  { to: '/admins', label: 'Administradores', icon: 'fa-user-shield', superOnly: true, group: 'Gestão' },
  { to: '/security/login-blocks', label: 'Bloqueios de login', icon: 'fa-shield-halved', superOnly: true, group: 'Segurança' },
  { to: '/monitoring', label: 'Monitoramento', icon: 'fa-heart-pulse', group: 'Operação' },
  { to: '/notifications', label: 'Notificações', icon: 'fa-bell', group: 'Operação' },
  { to: '/mavi', label: 'Mavi', icon: 'fa-wand-magic-sparkles', group: 'Operação' },
  { to: '/prices', label: 'Tabelas de preço', icon: 'fa-tags', group: 'Financeiro' },
  { to: '/billing', label: 'Faturamento', icon: 'fa-file-invoice-dollar', group: 'Financeiro' },
]

const navRevenda = [
  { to: '/', label: 'Visão geral', icon: 'fa-table-cells-large', end: true, group: 'Painel' },
  { to: '/tenants', label: 'Minhas bases', icon: 'fa-building', group: 'Gestão' },
]

function useIsMobile() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 1023px)').matches : false,
  )
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)')
    const fn = () => setMobile(mq.matches)
    mq.addEventListener('change', fn)
    return () => mq.removeEventListener('change', fn)
  }, [])
  return mobile
}

export default function AdminLayout() {
  const { admin, logout, isMaster, isSuperAdmin } = useAdminAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const [menuOpen, setMenuOpen] = useState(false)

  const links = isMaster ? navMaster.filter((l) => !l.superOnly || isSuperAdmin) : navRevenda

  const roleLabel = {
    super_admin: 'Super Admin',
    support: 'Suporte',
    revenda: 'Revenda',
  }
  const activeItem = links
    .filter((item) => item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to))
    .at(-1)
  const pageTitle = location.pathname === '/profile' ? 'Meu perfil' : activeItem?.label || 'M-Panel'
  const pageIcon = location.pathname === '/profile' ? 'fa-user' : activeItem?.icon || 'fa-table-cells-large'
  const initials = (admin?.email || 'MP').slice(0, 2).toUpperCase()

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isMobile) setMenuOpen(false)
  }, [isMobile])

  useEffect(() => {
    if (!menuOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [menuOpen])

  const sidebarContent = (
  <>
    <div className="relative px-5 pb-5 pt-6">
      <div className="flex items-center gap-3">
        <span className="mp-brand-mark grid h-12 w-12 place-items-center rounded-2xl border border-white/15 text-lg shadow-lg backdrop-blur">
          <i className="fas fa-shield-halved" aria-hidden="true" />
        </span>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-200">MAMControl</p>
          <h1 className="text-xl font-extrabold tracking-tight text-white">M-Panel</h1>
          <p className="mt-0.5 text-[10px] text-blue-200/70">Control center</p>
        </div>
      </div>
    </div>
    <nav className="mp-scrollbar flex-1 overflow-y-auto overscroll-contain px-3 pb-4" aria-label="Navegação administrativa">
      {links.map((item, index) => (
        <div key={item.to}>
          {(index === 0 || links[index - 1]?.group !== item.group) && (
            <p className="mb-2 mt-5 px-3 text-[9px] font-extrabold uppercase tracking-[.2em] text-blue-200/45 first:mt-2">
              {item.group}
            </p>
          )}
        <NavLink
          to={item.to}
          end={item.end}
          onClick={() => setMenuOpen(false)}
          className={({ isActive }) =>
            clsx(
              'mp-nav-item group flex min-h-[46px] items-center gap-3 rounded-xl border px-3 py-2.5 text-[13px] font-semibold transition',
              isActive
                ? 'mp-nav-item-active text-white'
                : 'mp-nav-item-idle text-blue-100 hover:text-white',
            )
          }
        >
          {({ isActive }) => (
            <>
              <span className={clsx('grid h-8 w-8 place-items-center rounded-[10px] transition', isActive ? 'bg-white/15 text-white' : 'bg-white/[.045] text-blue-100 group-hover:bg-white/10')}>
                <i className={clsx('fas', item.icon)} aria-hidden="true" />
              </span>
              <span>{item.label}</span>
            </>
          )}
        </NavLink>
        </div>
      ))}
    </nav>
    <div className="border-t border-white/[.08] p-3.5">
      <div className="mp-user-panel rounded-2xl border border-white/10 p-3">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-cyan-300 to-blue-500 text-xs font-extrabold text-white shadow-lg">{initials}</span>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-white">{admin?.email}</p>
          <p className="mt-0.5 text-[10px] font-medium text-blue-200/75">{roleLabel[admin?.role] || admin?.role}</p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        <button
          type="button"
          onClick={() => {
            setMenuOpen(false)
            navigate('/profile')
          }}
          className="min-h-[38px] rounded-xl px-2 text-xs font-semibold text-blue-100 transition hover:bg-white/10 hover:text-white"
        >
          <i className="fas fa-user mr-1.5" aria-hidden="true" />Perfil
        </button>
        <button
          type="button"
          onClick={() => {
            logout()
            navigate('/login')
          }}
          className="min-h-[38px] rounded-xl px-2 text-xs font-semibold text-red-200 transition hover:bg-red-400/10"
        >
          <i className="fas fa-arrow-right-from-bracket mr-2" aria-hidden="true" />Sair
        </button>
      </div>
      </div>
    </div>
  </>
  )

  return (
    <div className="mpanel-shell flex h-[100dvh] w-full overflow-hidden bg-brand-light">
      {!isMobile && (
        <aside className="mpanel-sidebar flex w-[278px] shrink-0 flex-col text-white">
          {sidebarContent}
        </aside>
      )}

      <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col">
        <header className="mpanel-topbar flex min-h-[76px] shrink-0 items-center gap-3 border-b px-4 py-2 backdrop-blur-xl sm:px-6 lg:px-8">
          {isMobile && (
            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center rounded-xl text-white transition hover:bg-white/10"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
            >
              <i className={clsx('fas', menuOpen ? 'fa-times' : 'fa-bars')} />
            </button>
          )}
          <span className="hidden h-10 w-10 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/[.07] text-sm text-cyan-100 sm:grid">
            <i className={`fas ${pageIcon}`} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-cyan-200/80">Painel administrativo</p>
            <p className="truncate text-sm font-bold text-white sm:text-base">{pageTitle}</p>
          </div>
          <div className="hidden items-center gap-2.5 rounded-full border border-white/10 bg-white/[.07] py-1.5 pl-2 pr-3 text-xs font-semibold text-white sm:flex">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-emerald-400/15"><span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,.10)]" /></span>
            Online
          </div>
        </header>
        <main className="mp-scrollbar flex-1 overflow-auto p-4 sm:p-6 lg:p-7 xl:p-9">
          <div className="mp-page-enter mx-auto w-full max-w-[1500px]">
            <Outlet />
          </div>
        </main>
      </div>

      {isMobile && menuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/60"
            onClick={() => setMenuOpen(false)}
            aria-hidden
          />
          <aside
            className="mpanel-sidebar fixed left-0 top-0 z-50 flex h-[100dvh] w-[min(300px,88vw)] flex-col text-white shadow-2xl"
            role="dialog"
            aria-modal
            aria-label="Menu M-Panel"
          >
            <div className="flex items-center justify-end border-b border-white/20 px-3 py-2">
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-white/80 hover:bg-white/10"
                onClick={() => setMenuOpen(false)}
                aria-label="Fechar menu"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            {sidebarContent}
          </aside>
        </>
      )}
    </div>
  )
}
